import React, { useState } from 'react';
import { Button, message, Modal, Tooltip } from '../../../components/AntComponents';
import Icon from '../../../components/AppIcon';
import { BroadcastStorage } from '@/utils/BroadcastStorage';
import { MediaSoupStopBroadcast, MediaSoupBroadcast, MediaMTXStopBroadcast } from '@/api/broadcastService';
import { mediasoupService } from '@/mediaservices/mediasoupService';
import { mediamtxService } from '@/mediaservices/mediamtxService';

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === "true";

export type BroadcastState = 'stopped' | 'live' | 'paused';

interface Props {
  state: BroadcastState;
  duration: string;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onSwitchRoles: () => void;
  hasHost?: boolean;
  hasCoHost?: boolean;
}

const BroadcastControls: React.FC<Props> = ({
  state, duration, onStart, onResume, onStop, onSwitchRoles,
  hasHost = false,
  hasCoHost = false,
}) => {
   const [isStoppingBroadcast, setIsStoppingBroadcast] = useState(false);
   const [isSwitchingRoles, setIsSwitchingRoles] = useState(false);

  const canSwitchRoles = hasHost && hasCoHost;
  //   Simplified - let parent handle all the logic
  const handleSwitchRoles = async () => {
    setIsSwitchingRoles(true);
    try {
      
      if (USE_MEDIAMTX) {
        // ✅ Always call with current streamId (or undefined for empty string)
        const streamId = BroadcastStorage.getStreamId();
        const hasActiveBroadcast = streamId && (state === 'live' || state === 'paused');
        
        
        // ✅ Pass streamId if we have one, otherwise let service use empty string
        mediamtxService.switchHostCoHost(
          hasActiveBroadcast ? streamId.toString() : undefined
        );
        
        message.success(
          hasActiveBroadcast 
            ? 'Role switch initiated for active broadcast'
            : 'Role switch initiated'
        );
        
      } else {
        const devices = BroadcastStorage.getSelectedDevices();
        const hasActiveBroadcast = devices.length > 0 && (state === 'live' || state === 'paused');
        
        if (hasActiveBroadcast) {
          // Get current roles before parent swaps them
          const { hostId, coHostId } = mediasoupService.getCurrentRoles();
          
          if (!hostId || !coHostId) {
            message.warning('Both host and co-host must be set to switch roles');
            return;
          }

          // Get current producer IDs
          const hostProducerIds = mediasoupService.getHostProducerIds();
          const coHostProducerIds = mediasoupService.getCoHostProducerIds();

          // Prepare payload with swapped roles
          const payload = {
            streamName: BroadcastStorage.getBroadcastName(),
            description: "Role switch update",
            devices: devices,
            hostDeviceSerial: {
              deviceSerial: coHostId, // Co-host becomes new host
              audioProducerId: coHostProducerIds[0] || '',
              videoProducerId: coHostProducerIds[1] || '',
            },
            coHostDeviceSerial: [{
              deviceSerial: hostId, // Host becomes new co-host
              audioProducerId: hostProducerIds[0] || '',
              videoProducerId: hostProducerIds[1] || '',
            }],
          };

          console.log('Switching roles with start broadcast payload:', payload);
          const response = await MediaSoupBroadcast(payload);
          BroadcastStorage.storeStreamId(response.streamId);
          console.log('Broadcast updated with new roles, new stream ID:', response.streamId);
        } else {
          // MediaSoup role switch without broadcast
          onSwitchRoles();
        }
        
        // Keep broadcast state as live during role switch
        if (hasActiveBroadcast) {
          BroadcastStorage.storeBroadcastState('live');
        }
        
        message.success('Roles switched successfully');
      }
      
    } catch (error: any) {
      console.error('Failed to switch roles:', error);
      message.error(error?.message || 'Failed to switch roles');
    } finally {
      setIsSwitchingRoles(false);
    }
  };

  const handleEndBroadcast = () => {
    // Get stored devices for confirmation
    const storedDevices = BroadcastStorage.getSelectedDevices();
    const deviceCount = storedDevices.length;

    Modal.confirm({
      className: 'themed-confirm',
      title: 'End Broadcast',
      content: (
        <div>
          <p>Are you sure you want to end the current broadcast?</p>
        </div>
      ),
      okText: 'End Broadcast',
      okType: 'danger',
      cancelText: 'Cancel',
      confirmLoading: isStoppingBroadcast,
      onOk: async () => {
        setIsStoppingBroadcast(true);
        try {
          const devices = BroadcastStorage.getSelectedDevices();
          const streamId = BroadcastStorage.getStreamId();
          
          if (devices.length === 0) {
            message.warning('No devices found to stop broadcast for.');
            return;
          }

          if (!streamId) {
            message.warning('No stream ID found to stop broadcast.');
            return;
          }

          if (USE_MEDIAMTX) {
            //   For MediaMTX - call the stop broadcast API
            console.log("Stopping broadcast:", {streamId, devices});

            await MediaMTXStopBroadcast({
              streamId: streamId,
              devices: devices
            });
            
            message.success('Broadcast ended successfully');
          } else {
            // For MediaSoup - call the stop broadcast API
            console.log("Stopping MediaSoup broadcast:", {streamId, devices});

            await MediaSoupStopBroadcast({
              streamId: streamId,
              devices: devices
            });
            
            message.success('Broadcast ended successfully');
          }
          
          //   Clear stored state when ending broadcast
          BroadcastStorage.storeBroadcastState('stopped');
          BroadcastStorage.clearSelectedDevices();
          
          // Call parent onStop handler
          onStop();
          
        } catch (error: any) {
          console.error('Failed to stop broadcast:', error);
          message.error(error?.message || 'Failed to end broadcast');
        } finally {
          setIsStoppingBroadcast(false);
        }
      },
      onCancel: () => {
        // User cancelled, do nothing
      }
    });
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Status */}
      {state === 'live' && (
        <span className="text-sm font-mono text-error flex items-center">
          <Icon name="Clock" size={14} className="mr-1" /> {duration}
        </span>
      )}

      {/* Buttons */}
      {state === 'stopped' && (
        <Button type="primary" variant="default" onClick={onStart} iconName="Play" size="sm">
          Start Broadcast
        </Button>
      )}
      {state === 'live' && (
        <>
          <Button 
            variant="destructive" 
            onClick={handleEndBroadcast} 
            iconName="Square" 
            size="sm"
            loading={isStoppingBroadcast}
          >
            End Broadcast
          </Button>
        </>
      )}
      
      {/* <Tooltip 
        title={!canSwitchRoles ? "Both Host and Co-Host must be assigned" : "Switch Host and Co-Host roles"}
      >
        <Button
          variant="outline"
          onClick={handleSwitchRoles}
          iconName="Swap"
          size="sm"
          loading={isSwitchingRoles}
          disabled={!canSwitchRoles}
        >
          Switch Host & Co-Host
        </Button>
      </Tooltip> */}
    </div>
  );
};

export default BroadcastControls;
